import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import en from "./en.json";
import hi from "./hi.json";
import bn from "./bn.json";
import gu from "./gu.json";
import mr from "./mr.json";
import ta from "./ta.json";
import te from "./te.json";
import kn from "./kn.json";
import pa from "./pa.json";
import or_ from "./or.json";
import ml from "./ml.json";

const LANG_STORAGE_KEY = "mmj_app_language";
const LANG_CACHE_PREFIX = "mmj_app_language_user_";

const savedLang = localStorage.getItem(LANG_STORAGE_KEY) || "en";

const enResource = en;
const languageResources = {
  en: enResource,
  hi,
  bn,
  gu,
  mr,
  ta,
  te,
  kn,
  pa,
  or: or_,
  ml,
};

i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enResource },
      hi: { translation: hi },
      bn: { translation: bn },
      gu: { translation: gu },
      mr: { translation: mr },
      ta: { translation: ta },
      te: { translation: te },
      kn: { translation: kn },
      pa: { translation: pa },
      or: { translation: or_ },
      ml: { translation: ml },
    },
    lng: savedLang,
    fallbackLng: "en",
    supportedLngs: Object.keys(languageResources),
    nonExplicitSupportedLngs: true,
    load: "languageOnly",
    interpolation: {
      escapeValue: false,
    },
  });

export default i18n;

export const SUPPORTED_LANGUAGES = [
  { code: "en", label: "English", nativeLabel: "English" },
  { code: "hi", label: "Hindi", nativeLabel: "हिंदी" },
  { code: "bn", label: "Bengali", nativeLabel: "বাংলা" },
  { code: "gu", label: "Gujarati", nativeLabel: "ગુજરાતી" },
  { code: "mr", label: "Marathi", nativeLabel: "मराठी" },
  { code: "ta", label: "Tamil", nativeLabel: "தமிழ்" },
  { code: "te", label: "Telugu", nativeLabel: "తెలుగు" },
  { code: "kn", label: "Kannada", nativeLabel: "ಕನ್ನಡ" },
  { code: "pa", label: "Punjabi", nativeLabel: "ਪੰਜਾਬੀ" },
  { code: "or", label: "Odia", nativeLabel: "ଓଡିଆ" },
  { code: "ml", label: "Malayalam", nativeLabel: "മലയാളം" },
];

function flattenTextMap(input, out = {}) {
  if (!input || typeof input !== "object") return out;
  Object.values(input).forEach((value) => {
    if (typeof value === "string") {
      const key = value.trim();
      if (key) out[key] = value;
      return;
    }
    if (value && typeof value === "object") {
      flattenTextMap(value, out);
    }
  });
  return out;
}

const EN_TEXT_TO_KEY = flattenTextMap(enResource, {});
const LANGUAGE_TEXT_MAP_CACHE = new Map();

export function getUserLanguageCacheKey(userId) {
  const clean = String(userId || "").trim();
  return clean ? `${LANG_CACHE_PREFIX}${clean}` : "";
}

export function resolveCachedLanguageForUser(userId) {
  const userKey = getUserLanguageCacheKey(userId);
  if (userKey) {
    const byUser = String(localStorage.getItem(userKey) || "").trim().toLowerCase();
    if (byUser) return byUser;
  }
  return String(localStorage.getItem(LANG_STORAGE_KEY) || "en").trim().toLowerCase() || "en";
}

export function cacheLanguageForUser(userId, langCode) {
  const code = String(langCode || "en").trim().toLowerCase() || "en";
  const userKey = getUserLanguageCacheKey(userId);
  if (userKey) localStorage.setItem(userKey, code);
  localStorage.setItem(LANG_STORAGE_KEY, code);
}

export function getUiPhraseTranslationMap(langCode) {
  const code = String(langCode || "en").trim().toLowerCase() || "en";
  if (LANGUAGE_TEXT_MAP_CACHE.has(code)) return LANGUAGE_TEXT_MAP_CACHE.get(code);
  const target = flattenTextMap(languageResources[code] || enResource, {});
  const map = new Map();
  Object.keys(EN_TEXT_TO_KEY).forEach((enText) => {
    const translated = target[enText];
    if (translated && translated !== enText) {
      map.set(enText, translated);
    }
  });
  LANGUAGE_TEXT_MAP_CACHE.set(code, map);
  return map;
}

